import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { ModalScreen } from '@euler/app/tv/components/ModalScreen';
import { Colors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { VehicleServiceRecord } from '@euler/model';
import { ResourceAccessScope } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { formatDateTime } from '@euler/utils/formatters';
import { MotiView } from 'moti';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  FlatList,
  StyleProp,
  TouchableOpacity,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';

const kCellMargin = 16;
const kMaxCellWidth = 200;

const useRecordListMetrics = () => {
  const { rpx } = useResponsiveContext();
  const { width } = useWindowDimensions();
  const cellMargin = rpx(kCellMargin);
  const columns = Math.floor(width / (rpx(kMaxCellWidth) + cellMargin));
  const [cellWidth, cellHeight] = computeGridLayoutItemSize({
    numberOfColumns: columns + 0.25,
    gridWidth: width,
    cellMargin: { left: 0, top: 0, bottom: 0, right: cellMargin },
    aspectRatio: 1,
    gridPadding: 0,
  });
  return { cellWidth, cellHeight, cellMargin };
};

export const ServiceRecordsScreen = wrapNavigatorScreen(
  () => {
    const appLoading = useAppLoading();

    const { rpx } = useResponsiveContext();
    const { detail } = useTaskContext();
    const { vehicleInfoService } = useServiceFactory();
    const { licensePlateNo, vin } = detail.basicInfo;

    const [serviceRecords, setServiceRecords] =
      useState<VehicleServiceRecord[]>();

    useEffect(() => {
      void appLoading.showWithAction(async () => {
        const res = await vehicleInfoService.listVehicleServiceInfo({
          scope: ResourceAccessScope.Org,
          plateno: licensePlateNo,
          vin,
        });
        setServiceRecords(res?.serviceRecords ?? []);
      });
    }, [appLoading, licensePlateNo, vehicleInfoService, vin]);

    const { cellWidth, cellHeight, cellMargin } = useRecordListMetrics();

    return (
      <ModalScreen
        css={`
          justify-content: center;
          align-items: center;
        `}
      >
        {serviceRecords != null && serviceRecords.length === 0 ? (
          <EmptyPlaceholder
            text="暂无历史服务记录"
            iconSize={rpx(40)}
            textStyle={{
              fontSize: rpx(12),
              lineHeight: rpx(38),
            }}
            css={`
              flex: 1;
            `}
          />
        ) : (
          <FlatList
            data={serviceRecords ?? []}
            keyExtractor={x => x._id}
            horizontal
            showsHorizontalScrollIndicator={false}
            renderItem={({ item, index }) => (
              <Cell
                record={item}
                index={index}
                css={`
                  width: ${cellWidth}px;
                  height: ${cellHeight}px;
                  margin-right: ${cellMargin}px;
                  background-color: #191919;
                  border-radius: ${rpx(12)}px;
                  overflow: hidden;
                  elevation: 5;
                  padding: ${rpx(12)}px;
                `}
              />
            )}
            contentContainerStyle={{
              padding: cellMargin,
              alignItems: 'center',
            }}
            css={`
              flex-grow: 0;
              height: 100%;
            `}
          />
        )}
      </ModalScreen>
    );
  },
  {
    animationEnabled: true,
    presentation: 'transparentModal',
    cardStyle: {
      backgroundColor: 'transparent',
    },
  },
);

const Cell = memo(
  ({
    record,
    style,
    index,
    onPress,
  }: {
    record: VehicleServiceRecord;
    style?: StyleProp<ViewStyle>;
    index: number;
    onPress?: (record: VehicleServiceRecord) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(() => {
      onPress?.(record);
    }, [onPress, record]);

    return (
      <MotiView
        style={style}
        from={{
          opacity: 0,
          translateY: rpx(120),
        }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={index * 100}
      >
        <TouchableOpacity
          onPress={handlePress}
          css={`
            flex: 1;
            justify-content: center;
          `}
        >
          <Info label="进店里程" text={`${record.mileage}公里`} />
          <Info label="服务单号" text={`${record.orderNo}`} />
          <Info
            label="进店时间"
            text={`${formatDateTime(record.serviceTime)}`}
          />
          <Info
            label="完工时间"
            text={`${formatDateTime(record.finishedAt)}`}
          />
          <Info label="服务顾问" text={`${record.serviceAgentName}`} />
          <Info label="服务项目" text={`${record.items?.length ?? 0}项`} />
          <Info
            label="遗留问题"
            text={`${record.pendingIssuesV2?.length ?? 0}个`}
          />
        </TouchableOpacity>
      </MotiView>
    );
  },
);

const Info = memo(
  ({
    label,
    text,
    style,
  }: {
    label: string;
    text: string;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <HStack justifyContent="flex-start" style={style}>
        <Typography.Label regular color={Colors.White3} size={rpx(12)}>
          {`${label}: `}
        </Typography.Label>
        <Typography.Label regular color={Colors.White7_5} size={rpx(12)}>
          {text}
        </Typography.Label>
      </HStack>
    );
  },
);
